import { Checkbox, Divider, Grid, Typography } from '@material-ui/core';
import moment from 'moment';
import React from 'react';

import { DashboardEventTypes } from '~/src/dash/analytics/analytics';
import { Calendar } from '~/src/dash/components/calendar/calendar';
import { IFocusedInput } from '~/src/dash/components/calendar/calendar-wrapper';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IMenuList } from '~/src/dash/types/menuList';
import { ICalendarQuickSelectorOptions } from '~/src/dash/utils/calendar';
import { publicaColors } from '~/src/dash/utils/colors';
import { formatDateFromUTCToOffset, formatWithoutHoursMins } from '~/src/dash/utils/date';
import {
  findCompareEndDate,
  findCompareStartDate,
  generateCalendarQuickSelectorCustomOptions,
  generateCalendarQuickSelectorOptions,
  isAnyFilterActive,
} from '~/src/dash/views/common/ias-data-consts';
import { IIASDashboardFilter } from '~/src/dash/views/ias-publisher/IASDashboard/common/types';
import { IASDashboardFiltersPill } from '~/src/dash/views/ias-publisher/IASDashboard/IASDashboardFilters/IASDashboardFiltersPill';

interface IIASDashboardCalendarProps {
  calStartDate: moment.Moment;
  calEndDate: moment.Moment;
  calDisplayStartDate: moment.Moment;
  calDisplayEndDate: moment.Moment;
  compStartDate: moment.Moment;
  compEndDate: moment.Moment;
  compDisplayStartDate: moment.Moment;
  compDisplayEndDate: moment.Moment;
  calFocusedInput?: IFocusedInput;
  compFocusedInput?: IFocusedInput;
  range1Selected: string;
  range2Selected: string;
  range1DisplaySelected: string;
  range2DisplaySelected: string;
  handleChangeAggRangeSelected: (
    rangeSelected: string,
    isCompareCalendar: boolean,
    applied?: boolean,
  ) => void;
  handleCalendarDateChange: (
    isCompareCalendar: boolean,
  ) => (start: moment.Moment, end: moment.Moment, isCustom: boolean) => void;
  handleDateRangeChange: (
    start: moment.Moment,
    end: moment.Moment,
    isCompareCalendar: boolean,
  ) => void;
  handleCalendarFocusedInputChange: (
    isCompareCalendar: boolean,
  ) => (focusedInput: IFocusedInput) => void;
  isComparedToEnabled: boolean;
  handleCompareEnabled: (event: any, checked: boolean) => void;
  trackEvent: (eventTitle: DashboardEventTypes) => void;
}

interface IIASDashboardFiltersProps extends IIASDashboardCalendarProps {
  session: ISessionState;
  timezone: string;
  // filters
  filters: IIASDashboardFilter[];
  handleFilterOnChange: (menuTitle: string) => (values: IMenuList) => void;
  handleFilterAccept: () => void;
  handleClearFilters: () => void;
}

export const IASDashboardFilters = ({
  session,
  timezone,
  // calendar props
  range1Selected,
  range1DisplaySelected,
  range2Selected,
  range2DisplaySelected,
  compStartDate,
  compEndDate,
  compDisplayStartDate,
  compDisplayEndDate,
  calStartDate,
  calEndDate,
  calDisplayStartDate,
  calDisplayEndDate,
  calFocusedInput,
  compFocusedInput,
  // calendar functions
  handleChangeAggRangeSelected,
  handleCalendarDateChange,
  handleDateRangeChange,
  handleCalendarFocusedInputChange,
  isComparedToEnabled,
  handleCompareEnabled,
  // filter functions
  filters,
  handleFilterOnChange,
  handleFilterAccept,
  trackEvent,
  handleClearFilters,
}: IIASDashboardFiltersProps) => {
  const compareQuickSelectorOptions: ICalendarQuickSelectorOptions[] = [
    {
      title: 'Previous Period',
      date1: () =>
        findCompareStartDate({
          timezone,
          startDate: calDisplayStartDate,
          endDate: calDisplayEndDate,
          rangeSelected: range1DisplaySelected,
        }),
      date2: () =>
        findCompareEndDate({
          timezone,
          startDate: calDisplayStartDate,
          endDate: calDisplayEndDate,
          rangeSelected: range1DisplaySelected,
        }),
    },
    {
      title: 'Last Month',
      date1: () => moment(calDisplayStartDate).tz(timezone).subtract(1, 'months'),
      date2: () => moment(calDisplayEndDate).tz(timezone).subtract(1, 'months'),
    },
  ];

  const compareQuickSelectorCustomOptions: ICalendarQuickSelectorOptions[] = [
    {
      title: ' Days Ago, Same Period',
      date1: (value: string) =>
        moment(calDisplayStartDate).tz(timezone).subtract(parseInt(value, 10), 'days'),
      date2: (value: string) =>
        moment(calDisplayEndDate).tz(timezone).subtract(parseInt(value, 10), 'days'),
    },
    {
      title: ' Weeks Ago, Same Period',
      date1: (value: string) =>
        moment(calDisplayStartDate).tz(timezone).subtract(parseInt(value, 10), 'weeks'),
      date2: (value: string) =>
        moment(calDisplayEndDate).tz(timezone).subtract(parseInt(value, 10), 'weeks'),
    },
    {
      title: ' Months Ago, Same Period',
      date1: (value: string) =>
        moment(calDisplayStartDate).tz(timezone).subtract(parseInt(value, 10), 'months'),
      date2: (value: string) =>
        moment(calDisplayEndDate).tz(timezone).subtract(parseInt(value, 10), 'months'),
    },
  ];

  const isCalDayHighlighted = (day: moment.Moment): boolean => {
    if (!isComparedToEnabled) {
      return false;
    }
    return formatDateFromUTCToOffset(day.format('YYYY-MM-DDTHH:mm:ss'), 'UTC').isBetween(
      compDisplayStartDate,
      compDisplayEndDate,
      'day',
      '[]',
    );
  };

  const isCompDayHighlighted = (day: moment.Moment): boolean => {
    return formatDateFromUTCToOffset(day.format('YYYY-MM-DDTHH:mm:ss'), 'UTC').isBetween(
      calDisplayStartDate,
      calDisplayEndDate,
      'day',
      '[]',
    );
  };

  return (
    <Grid container direction='column' spacing={2}>
      {/* Row One */}
      <Grid
        item
        container
        spacing={1}
        justify='flex-start'
        alignItems='center'
        style={{
          paddingTop: 0,
          height: 48,
        }}
      >
        <Grid item style={{ paddingRight: 12, marginTop: 4 }}>
          <img src='/assets/shared/svg/Calendar.svg' />
        </Grid>
        <Grid item justify='flex-start' style={{ paddingRight: 12 }}>
          <Typography style={{ fontSize: 14, fontWeight: 500 }}>Period</Typography>
        </Grid>
        <Grid item justify='flex-start'>
          <Typography style={{ fontSize: 12 }}>{range1DisplaySelected}</Typography>
        </Grid>
        <Grid item>
          <Calendar
            startDate={calStartDate}
            endDate={calEndDate}
            calStartDate={calDisplayStartDate}
            calEndDate={calDisplayEndDate}
            startDateID='startDate'
            endDateID='endDate'
            rangeSelected={range1Selected}
            handleRangeSelected={handleChangeAggRangeSelected}
            handleCalendarDateChange={handleCalendarDateChange(false)}
            handleDateRangeChange={handleDateRangeChange}
            quickSelectorOptions={generateCalendarQuickSelectorOptions(timezone)}
            quickSelectorCustomOptions={generateCalendarQuickSelectorCustomOptions(timezone)}
            isDayHighlighted={isCalDayHighlighted}
            isCompareCalendar={false}
            focusedInput={calFocusedInput || null}
            excludeToday={true}
            dateFormat={formatWithoutHoursMins(session.publisher.Localization)}
            handleFocusedInputChange={handleCalendarFocusedInputChange(false)}
            trackOnClose={() => trackEvent('IAS Publisher Dashboard - Open Calendar')}
            timezone={timezone}
            anchorDirection='left'
          />
        </Grid>
        <Grid item>
          <Checkbox size='small' checked={isComparedToEnabled} onChange={handleCompareEnabled} />
        </Grid>
        <Grid item>
          <Typography style={{ fontSize: 12 }}>{range2DisplaySelected}</Typography>
        </Grid>
        <Grid item>
          {/* Comparison Calendar */}
          <Calendar
            startDate={compStartDate}
            endDate={compEndDate}
            calStartDate={compDisplayStartDate}
            calEndDate={compDisplayEndDate}
            startDateID='startDate'
            endDateID='endDate'
            rangeSelected={range2Selected}
            handleRangeSelected={handleChangeAggRangeSelected}
            handleCalendarDateChange={handleCalendarDateChange(true)}
            handleDateRangeChange={handleDateRangeChange}
            quickSelectorOptions={compareQuickSelectorOptions}
            quickSelectorCustomOptions={compareQuickSelectorCustomOptions}
            isDayHighlighted={isCompDayHighlighted}
            isCompareCalendar={true}
            focusedInput={compFocusedInput || null}
            excludeToday={true}
            dateFormat={formatWithoutHoursMins(session.publisher.Localization)}
            handleFocusedInputChange={handleCalendarFocusedInputChange(true)}
            trackOnClose={() => trackEvent('IAS Publisher Dashboard - Open Compare Calendar')}
            timezone={timezone}
            anchorDirection='left'
          />
        </Grid>
      </Grid>
      <Grid item>
        <Divider
          variant='fullWidth'
          style={{ height: '1px', padding: 0 }}
          orientation='horizontal'
        />
      </Grid>
      {/* Row Two */}
      <Grid
        item
        container
        justify='flex-start'
        alignItems='center'
        spacing={2}
        direction='row'
        style={{
          paddingTop: 0,
          height: 64,
        }}
      >
        <Grid item style={{ marginTop: 4 }}>
          <img src='/assets/shared/svg/FilterDropdownRoot.svg' />
        </Grid>
        <Grid item>
          <Typography style={{ fontSize: 14, fontWeight: 500 }}>Filters</Typography>
        </Grid>
        {filters.map((filter: any) => {
          return (
            <Grid item>
              <IASDashboardFiltersPill
                menuTitle={filter.menuTitle}
                list={filter.list}
                handleFilterOnChange={handleFilterOnChange}
                handleFilterAccept={handleFilterAccept}
                trackEvent={() => trackEvent('IAS Publisher Dashboard - Open Filter Pill')}
              />
            </Grid>
          );
        })}
        {isAnyFilterActive(filters) && (
          <Grid item onClick={handleClearFilters}>
            <Typography
              style={{
                marginLeft: 10,
                fontSize: '0.875rem',
                fontWeight: 400,
                color: publicaColors.lightBlue,
                textTransform: 'none',
                cursor: 'pointer',
              }}
            >
              Clear
            </Typography>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
